// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The second opinion, and the pictures.
 *
 * peelback's own parser (src/pdf/) is strict on purpose: it refuses anything it
 * does not understand, because "this cross-reference table parses" is the
 * evidence a recovered revision rests on. pdf.js is the opposite by design —
 * when a file's cross-reference table does not parse it silently re-indexes
 * every object and opens the file anyway. That behaviour is right for a viewer
 * and useless as a gate, so pdf.js is NOT asked whether a revision is real. It
 * is asked a different and complementary question: does this actually open,
 * and what does it look like?
 *
 * THIS IS NOT A PREFERENCE, IT IS THE ONLY ARRANGEMENT THAT WORKS. There is no
 * pdf.js option that turns the repair off. `stopAtErrors` sounds like one and
 * is not: it is converted to an `ignoreErrors` flag that is placed only inside
 * the content-stream evaluator's options, and an A/B test on a deliberately
 * broken prefix returns byte-identical results with it true and false. Worse,
 * `XRef.readXRef()` swallows per-section failures and only throws if it got no
 * trailer at all, so a candidate whose own table is fine but whose /Prev chain
 * is broken loads with no exception and no visible warning at all.
 *
 * And "it renders" is not evidence either, in any engine: sweeping 79
 * truncation points of a two-revision file through pdf.js, four structurally
 * invalid prefixes — cut in the MIDDLE of a cross-reference table — loaded
 * cleanly and returned plausible text. Apple's CoreGraphics produced a
 * thumbnail for the same invalid bytes. A second renderer cannot rescue the
 * gate; only the strict parse can. So this file can add a caveat to a revision
 * and can never promote one.
 *
 * The picture is the point. A metadata finding is an inference the reader has
 * to trust. Two renderings of page two, side by side, with a different number
 * on each, is something the reader can see. Everything else in this tool is
 * supporting evidence for that one moment.
 *
 * Runs on the main thread because pdf.js brings its own worker; the tab does
 * not block.
 */

import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url,
).href;

/*
 * A browser suspends `requestAnimationFrame` entirely while a tab is in the
 * background — no throttling, no late call, it simply never fires. pdf.js
 * schedules the continuation of a page render with rAF, so a render that is
 * running when somebody switches tabs stops mid-page and never resumes.
 *
 * That is a real thing people do: open a PDF, start reading two versions side
 * by side, switch to their email to check something, come back to a spinner
 * that will never finish. Measured in this project's own preview browser,
 * where the tab is permanently hidden: timers fired (throttled to ~1.2 s),
 * microtasks and fetch worked, and rAF never fired once.
 *
 * So every rAF here also arms a timer. Whichever arrives first wins, and the
 * other is ignored. Cancelling is safe: pdf.js's render task checks its own
 * `cancelled` flag before doing anything, so a timer that fires after a
 * cancelled frame does nothing.
 */
function installFrameFallback() {
  if (typeof window === 'undefined' || (window as { __peelbackRaf?: boolean }).__peelbackRaf) return;
  (window as { __peelbackRaf?: boolean }).__peelbackRaf = true;

  const native = window.requestAnimationFrame.bind(window);
  window.requestAnimationFrame = (callback: FrameRequestCallback): number => {
    let fired = false;
    const once = (time: number) => {
      if (fired) return;
      fired = true;
      callback(time);
    };
    const handle = native(once);
    window.setTimeout(() => once(performance.now()), 200);
    return handle;
  };
}

installFrameFallback();

/**
 * pdf.js is a second opinion, and a second opinion that never arrives must not
 * stop the tool. Its worker can wedge — a wedged one has been observed here in
 * a backgrounded tab — and the whole analysis would then sit behind it
 * forever. Everything below is bounded, and a timeout is reported as "could
 * not be checked" rather than as a fault in the document.
 */
const PDFJS_BUDGET_MS = 15_000;

function withBudget<T>(work: Promise<T>, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([
    work,
    new Promise<T>((resolve) => {
      timer = setTimeout(() => resolve(fallback), PDFJS_BUDGET_MS);
    }),
  ]).finally(() => clearTimeout(timer)) as Promise<T>;
}

export interface OpenResult {
  /**
   * `true`  — the second engine opened it.
   * `false` — the second engine actively rejected it.
   * `null`  — the check could not be run at all.
   *
   * The third state matters, and collapsing it into `false` was a real bug in
   * this file. A revision peelback's own strict parser has accepted is a
   * revision; if the corroborating engine merely fails to answer, that is a
   * fact about the engine and not about the document, and withholding the
   * user's file over it would be wrong. Only an active rejection withholds.
   */
  ok: boolean | null;
  pageCount: number | null;
  error: string | null;
}

const TIMED_OUT: OpenResult = {
  ok: null,
  pageCount: null,
  error:
    'the second PDF engine did not answer within 15 seconds, so this check could not be completed. ' +
    'peelback’s own reader accepted this version',
};

const COMMON: Record<string, unknown> = {
  // No scripting, no eval, no system font access. None of it is needed to
  // establish that a file opens and to draw it.
  isEvalSupported: false,
  enableXfa: false,
  disableAutoFetch: true,
  useSystemFonts: false,
  disableFontFace: false,
  // A PDF may not fetch anything. A file that tried would be doing so from the
  // reader's network, which is precisely what this tool promises does not
  // happen.
  useWorkerFetch: false,
  // Kept because it does make the content-stream evaluator strict, which is
  // where it genuinely applies. It does NOT disable cross-reference recovery —
  // see the note at the top of this file.
  stopAtErrors: true,
  // pdf.js writes warnings straight to the console, and a warning can carry a
  // fragment of the document's own content. Every file this tool opens is
  // somebody's payslip or bank statement, so the channel is closed rather than
  // filtered. (0 is VerbosityLevel.ERRORS's predecessor, `ERRORS: 0` — nothing
  // below an error is logged, and errors are returned to the caller anyway.)
  verbosity: 0,
};

/**
 * The loading task rather than the document: `destroy()` lives on the task and
 * is what actually tears the worker down. Destroying the document proxy alone
 * leaves the worker and its heap behind, and a file with forty saves then
 * exhausts the tab.
 */
function open(bytes: Uint8Array): PDFDocumentLoadingTask {
  // pdf.js takes ownership of the array it is handed, so it gets its own copy
  // and the caller keeps the file.
  return pdfjs.getDocument({ data: bytes.slice(), ...COMMON } as never);
}

/**
 * Opens a recovered revision and reports whether it is a working PDF.
 *
 * This never *promotes* a revision — peelback's own parser has already decided
 * that. It can only add a caveat, which is the safe direction: a revision that
 * peelback believes in but pdf.js cannot open is reported with that said out
 * loud, rather than handed over as though nothing were wrong.
 */
export function openRevision(bytes: Uint8Array): Promise<OpenResult> {
  return withBudget(openRevisionUnbounded(bytes), TIMED_OUT);
}

async function openRevisionUnbounded(bytes: Uint8Array): Promise<OpenResult> {
  let task: PDFDocumentLoadingTask | null = null;
  try {
    task = open(bytes);
    const doc: PDFDocumentProxy = await task.promise;
    const pageCount = doc.numPages;
    if (pageCount < 1) {
      return { ok: false, pageCount, error: 'it opens but contains no pages' };
    }
    // Opening is not enough — the first page has to be interpretable too.
    const page = await doc.getPage(1);
    await page.getOperatorList();
    page.cleanup();
    return { ok: true, pageCount, error: null };
  } catch (err) {
    return { ok: false, pageCount: null, error: (err as Error)?.message ?? 'it could not be opened' };
  } finally {
    // Not awaited. A wedged worker makes `destroy()` hang on its own Terminate
    // round-trip, and awaiting it here would put that hang in front of every
    // remaining revision. The task tears itself down when it can.
    void task?.destroy().catch(() => undefined);
  }
}

export interface RenderedPage {
  pageNumber: number;
  width: number;
  height: number;
  dataUrl: string;
}

/**
 * Draws one page of a revision to a PNG data URL.
 *
 * A data URL rather than an object URL on purpose: object URLs have to be
 * revoked, and a page that forgets leaks the contents of somebody's bank
 * statement for as long as the tab is open. A data URL dies with the element.
 */
export function renderPage(
  bytes: Uint8Array,
  pageNumber: number,
  maxWidth: number,
): Promise<RenderedPage | null> {
  return withBudget(renderPageUnbounded(bytes, pageNumber, maxWidth), null);
}

async function renderPageUnbounded(
  bytes: Uint8Array,
  pageNumber: number,
  maxWidth: number,
): Promise<RenderedPage | null> {
  let task: PDFDocumentLoadingTask | null = null;
  try {
    task = open(bytes);
    const doc = await task.promise;
    const clamped = Math.min(Math.max(1, pageNumber), doc.numPages);
    const page = await doc.getPage(clamped);

    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(maxWidth / base.width, 2.5);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));
    const context = canvas.getContext('2d');
    if (!context) return null;

    // A PDF page is transparent where nothing is drawn; on a dark background
    // an unfilled page would be an invisible rectangle.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvas, canvasContext: context, viewport } as never).promise;
    const dataUrl = canvas.toDataURL('image/png');
    page.cleanup();

    return { pageNumber: clamped, width: canvas.width, height: canvas.height, dataUrl };
  } catch {
    return null;
  } finally {
    void task?.destroy().catch(() => undefined);
  }
}

/** The text of one page, used to show what changed between two saves. */
export function pageText(bytes: Uint8Array, pageNumber: number): Promise<string | null> {
  return withBudget(pageTextUnbounded(bytes, pageNumber), null);
}

async function pageTextUnbounded(bytes: Uint8Array, pageNumber: number): Promise<string | null> {
  let task: PDFDocumentLoadingTask | null = null;
  try {
    task = open(bytes);
    const doc = await task.promise;
    const clamped = Math.min(Math.max(1, pageNumber), doc.numPages);
    const page = await doc.getPage(clamped);
    const content = await page.getTextContent();
    const parts: string[] = [];
    let lastY: number | null = null;
    for (const item of content.items) {
      if (!('str' in item)) continue;
      const y = Math.round((item.transform?.[5] as number) ?? 0);
      if (lastY !== null && Math.abs(y - lastY) > 2) parts.push('\n');
      parts.push(item.str);
      if (item.hasEOL) parts.push('\n');
      lastY = y;
    }
    page.cleanup();
    return parts.join('').replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
  } catch {
    return null;
  } finally {
    void task?.destroy().catch(() => undefined);
  }
}
